import InputField from "components/fields/InputField";
import Modal from "components/modal";
import Button from "components/button";

import React, { useEffect, useMemo } from "react";
import { useForm, Controller } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import LoaderButton from "components/button/LoaderButton";
import { editUsers } from "store/userSlice";
import SelectField from "components/fields/SelectField";

function EditUser({ userData, isOpen, onClose, refreshUserList }) {
  const dispatch = useDispatch();
  const { editError, editLoading, roleData } = useSelector(
    (state) => state.users
  );
  const activeRolesOptions = useMemo(() => {
    if (!Array.isArray(roleData) || roleData.length === 0) {
      return [];
    }

    return roleData
      .filter((role) => role.isActive)
      .map((role) => ({
        value: role.id,
        label: role.name,
      }));
  }, [roleData]);
  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
    reset,
  } = useForm({
    defaultValues: {
      firstname: "",
      lastname: "",
      email: "",
      mobile: "",
      roleId: "",
    },
    mode: "onBlur",
  });

  useEffect(() => {
    if (userData) {
      reset({
        _id: userData._id,
        firstname: userData.firstname || "",
        lastname: userData.lastname || "",
        email: userData.email || "",
        mobile: userData.mobile || "",
        roleId: userData.roleId || "",
      });
    }
  }, [userData, reset]);
  const onSubmit = async (data) => {
    try {
      const response = await dispatch(editUsers(data));

      // Check for success
      if (
        response?.payload?.statuscode === 201 ||
        response?.payload?.statuscode === 200
      ) {
        refreshUserList();
        onClose();
        reset();
      } else {
        console.error(
          "Create failed:",
          response?.payload?.msg || "Unknown error"
        );
      }
    } catch (error) {
      console.error("Create failed:", error);
    }
  };

  return (
    <div>
      <Modal isOpen={isOpen} onClose={onClose} className="max-w-4xl">
        <h2 className="border-b py-3 pl-2 text-xl font-bold text-navy-700 dark:text-white">
          Add User
        </h2>

        <div className="my-5 w-full max-w-4xl flex-col items-center lg:pl-0">
          <form
            onSubmit={handleSubmit(onSubmit)}
            className="mx-auto grid w-full grid-cols-1 gap-4"
            noValidate
          >
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <InputField
                id="firstname"
                variant="auth"
                label="Firstname*"
                placeholder="Enter firstname"
                type="text"
                error={errors.firstname}
                {...register("firstname", {
                  required: "Firstname is required",
                })}
              />

              <InputField
                id="lastname"
                variant="auth"
                label="Lastname*"
                placeholder="Enter lastname"
                type="text"
                error={errors.lastname}
                {...register("lastname", { required: "Lastname is required" })}
              />

              <InputField
                id="email"
                variant="auth"
                label="Email*"
                placeholder="mail@simmmple.com"
                type="email"
                autoComplete="email"
                error={errors.email}
                {...register("email", {
                  required: "Email is required",
                  pattern: {
                    value: /^\S+@\S+$/i,
                    message: "Invalid email address",
                  },
                })}
              />

              <InputField
                id="mobile"
                variant="auth"
                label="Mobile*"
                placeholder="9797979797"
                type="tel"
                autoComplete="tel"
                maxLength={10} // limit input to 10 digits
                error={errors.mobile}
                {...register("mobile", {
                  required: "Mobile number is required",
                  pattern: {
                    value: /^[0-9]{10}$/,
                    message: "Enter a valid 10-digit number",
                  },
                })}
                onInput={(e) => {
                  // Remove any non-numeric characters
                  e.currentTarget.value = e.currentTarget.value.replace(
                    /[^0-9]/g,
                    ""
                  );
                }}
              />

              <Controller
                name="roleId"
                control={control}
                rules={{ required: "User type is required" }}
                render={({ field }) => (
                  <SelectField
                    label="Role*"
                    options={activeRolesOptions}
                    placeholder="Select user role"
                    value={
                      field.value
                        ? activeRolesOptions.find(
                            (opt) => opt.value === field.value
                          )
                        : null
                    }
                    onChange={(selected) => field.onChange(selected?.value)}
                    error={errors.roleId}
                  />
                )}
              />
              {editError && (
                <div className="mt-2 grid grid-cols-1 gap-5 lg:grid-cols-1">
                  <p className="text-sm text-red-500">{editError}</p>
                </div>
              )}
            </div>

            <div className="flex items-center justify-end gap-2 pt-2">
              <Button type="button" variant="outline" onClick={onClose}>
                Cancel
              </Button>

              <LoaderButton type="submit" isLoading={editLoading}>
                Edit User
              </LoaderButton>
            </div>
          </form>
        </div>
      </Modal>
    </div>
  );
}

export default EditUser;
